import { CircularProgress, makeStyles } from '@material-ui/core';
import { DOCUMENT_TYPE } from 'doc-mate-store/lib/constants/loadDocument';
import React, { useRef, useState } from 'react';

type Props = {
  documentType: DOCUMENT_TYPE | string;
  isDisabled: boolean;
  isUploading?: boolean;
  onUpload: (documentType: any, files: File[]) => void;
  children: (
    openFileDialog: () => void,
    isUploading: boolean,
  ) => React.ReactNode;
};

const isInternalDrag = (e: React.DragEvent) => {
  if (!e.dataTransfer || !e.dataTransfer.types) return false;
  const types = Array.from(e.dataTransfer.types);
  return (
    types.includes('application/x-reorder-photo') ||
    types.includes('application/x-reorder-file')
  );
};

const isExternalFileDrag = (e: React.DragEvent) => {
  if (!e.dataTransfer || !e.dataTransfer.types) return false;
  if (isInternalDrag(e)) return false;
  const types = Array.from(e.dataTransfer.types);
  return types.includes('Files') || types.includes('file');
};

const DropzoneSection: React.FC<Props> = ({
  documentType,
  isDisabled,
  isUploading = false,
  onUpload,
  children,
}) => {
  const classes = useStyles();
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    if (isInternalDrag(e)) return;
    if (!isExternalFileDrag(e)) return;

    e.preventDefault();
    e.stopPropagation();
    if (isDisabled || isUploading) return;

    dragCounter.current += 1;
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (isInternalDrag(e)) return;

    e.preventDefault();
    e.stopPropagation();
    if (isDisabled || isUploading) return;

    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      setIsDragActive(false);
      dragCounter.current = 0;
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (isInternalDrag(e)) return;

    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = isDisabled || isUploading ? 'none' : 'copy';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    if (isInternalDrag(e)) return;

    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    dragCounter.current = 0;

    if (isDisabled || isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onUpload(documentType, Array.from(files));
    }
  };

  const openFileDialog = () => {
    if (isDisabled || isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(documentType, Array.from(e.target.files));
      e.target.value = '';
    }
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`${classes.dropzoneContainer} ${
        isDragActive ? classes.dropzoneActive : ''
      }`}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        disabled={isDisabled || isUploading}
        multiple
        accept="image/png, image/jpeg, application/pdf"
        onChange={handleFileInputChange}
      />

      {/* Uploading Overlay */}
      {isUploading && (
        <div className={classes.uploadingOverlay}>
          <CircularProgress size={24} style={{ color: '#0d374b' }} />
          <span className={classes.uploadingText}>Uploading files...</span>
        </div>
      )}

      {/* Dragging Overlay */}
      {isDragActive && !isUploading && (
        <div className={classes.dropzoneOverlay}>
          <span>📁 Drop photos or documents here to upload</span>
        </div>
      )}

      {children(openFileDialog, isUploading)}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  dropzoneContainer: {
    position: 'relative',
    borderRadius: 4,
    transition: 'all 0.2s ease-in-out',
    marginBottom: theme.spacing(1),
  },
  dropzoneActive: {
    outline: '2px dashed #0d374b',
    backgroundColor: 'rgba(13, 55, 75, 0.04)',
  },
  dropzoneOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: 14,
    color: '#0d374b',
    zIndex: 10,
    borderRadius: 4,
    border: '2px dashed #0d374b',
    pointerEvents: 'none',
  },
  uploadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    zIndex: 11,
    borderRadius: 4,
    backdropFilter: 'blur(1px)',
    cursor: 'not-allowed',
  },
  uploadingText: {
    fontWeight: 600,
    fontSize: 13,
    color: '#0d374b',
    marginLeft: 8,
  },
}));

export default DropzoneSection;
